import React, { useState } from 'react';
import { List, ListItemText, Divider, Paper, Typography, CircularProgress, Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { useAppSelector } from 'store/hooks';
import { appliedFiltersSelector } from 'store/filter/filterSelector';
import { convertDateToServerStrFormat } from 'utils/timeHelper';
import useDebounce from 'utils/useDebounce';
import type { IMessage, IUser } from 'api/services/dataEndpoints';
import { useLazyGetMessagesQuery } from 'api/services/dataEndpoints';
import Input from 'components/Input';

import { UserMessage, BotMessage, UserBubble, BotBubble } from './Elements';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface IMessagesListProps {
  selectedUser?: IUser | null;
}

const MessagesList: React.FC<IMessagesListProps> = ({ selectedUser }) => {
  const [search, setSearch] = useState('');
  const currentSearch = useDebounce(search);

  const [triggerGetMessages, { data: messages = [], isLoading, error }] = useLazyGetMessagesQuery();

  const filters = useAppSelector(appliedFiltersSelector);
  const trimmedCurrentSearch = currentSearch.trim();

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  React.useEffect(() => {
    if (selectedUser?.id) {
      triggerGetMessages({
        userId: selectedUser.id,
        dateRange: {
          from: convertDateToServerStrFormat(filters.dateRange.from),
          to: convertDateToServerStrFormat(filters.dateRange.to),
        },
        search: trimmedCurrentSearch || null,
      });
    }
  }, [
    selectedUser?.id,
    triggerGetMessages,
    filters.dateRange.from,
    filters.dateRange.to,
    trimmedCurrentSearch,
  ]);

  const renderMessage = (message: IMessage, index: number) => {
    const isUserMessage = message.sender === 'user';

    const MessageComponent = isUserMessage ? UserMessage : BotMessage;
    const BubbleComponent = isUserMessage ? UserBubble : BotBubble;
    const hasSessionCleared = message.deleted_at !== messages[index - 1]?.deleted_at && index > 0;

    return (
      <React.Fragment key={message.id}>
        <MessageComponent>
          <BubbleComponent>
            <ListItemText
              primary={message.message}
              secondary={new Date(message.created_at).toLocaleString()}
              secondaryTypographyProps={{
                color: 'text.secondary',
              }}
            />
          </BubbleComponent>
        </MessageComponent>
        <Divider />
        {hasSessionCleared && (
          <>
          <Typography variant="body2" gutterBottom sx={{ mt: 2, textAlign: 'center', color: 'red' }}>
            Session has been reset at {dayjs(messages[index - 1]?.deleted_at).format('MM-DD-YYYY HH:mm')}
          </Typography>
          <Divider />
          </>
        )}
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (!selectedUser?.id) {
      return <Typography>Choose a user</Typography>;
    }

    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Typography color="error">Cannnot load messages</Typography>;
    }

    if (messages.length === 0) {
      return <Typography>No messages</Typography>;
    }

    return <List>{messages.map(renderMessage)}</List>;
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, height: '100%' }}>
      {selectedUser?.id && (
        <>
          <Input
            nameInput="search"
            placeholder="Type to Search..."
            startAdornment={<SearchIcon />}
            value={search}
            onChangeValue={onChangeSearch}
          />
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Dialog for {selectedUser.custom_data.name}({selectedUser.email})
          </Typography>
        </>
      )}
      {renderContent()}
    </Paper>
  );
};

export default MessagesList;
